import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { supabaseAvailable } from "../auth/AuthProvider";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Card, CardHeader } from "../ui/Card";
import { LanguageSwitcher } from "../ui/LanguageSwitcher";

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = (location.state as { from?: string } | null)?.from ?? "/";
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { t } = useTranslation(["auth", "common"]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    setSubmitError(null);
    if (!supabase) {
      setSubmitError(t("supabaseNotConfigured"));
      return;
    }
    const { error } = await supabase.auth.signInWithPassword(values);
    if (error) {
      setSubmitError(error.message);
      return;
    }
    navigate(fromPath, { replace: true });
  }

  async function signInWithGoogle() {
    if (!supabase) return;
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader title={t("loginTitle")} />
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Input
            label={t("fields.email")}
            type="email"
            autoComplete="email"
            disabled={!supabaseAvailable}
            error={errors.email ? t("common:validation.email") : undefined}
            {...register("email")}
          />
          <Input
            label={t("fields.password")}
            type="password"
            autoComplete="current-password"
            disabled={!supabaseAvailable}
            error={errors.password ? t("common:validation.passwordMin", { count: 6 }) : undefined}
            {...register("password")}
          />
          {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}
          <Button type="submit" loading={isSubmitting} disabled={!supabaseAvailable}>
            {t("buttons.signIn")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={signInWithGoogle}
            disabled={!supabaseAvailable}
          >
            {t("buttons.continueWithGoogle")}
          </Button>
          <p className="text-center text-sm text-slate-500">
            {t("links.noAccount")}{" "}
            <Link to="/signup" className="text-brand-700 hover:underline">
              {t("links.createOne")}
            </Link>
          </p>
        </form>
      </Card>
    </AuthLayout>
  );
}

export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="flex flex-col items-center gap-6">
        <h1 className="text-brand-700 text-3xl font-bold">LawnOps</h1>
        {children}
        <LanguageSwitcher />
      </div>
    </main>
  );
}
